import {
  useMarkThreadAsResolved,
  useMarkThreadAsUnresolved,
} from "@liveblocks/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import type { ThreadPlacement } from "./threadPlacement";
import {
  pinClickClosesThreadWindow,
  type ThreadWindowSession,
} from "./threadWindow";

/**
 * The rail lives outside the RoomProvider and outside this provider, so its
 * two thread verbs cross the tree as window events — the same shape
 * `canvas:focus-node` and `canvas:remove-node` already use for rail rows.
 */
export const CANVAS_OPEN_COMMENT_THREAD_EVENT = "canvas:open-comment-thread";
export const CANVAS_RESOLVE_COMMENT_THREAD_EVENT =
  "canvas:resolve-comment-thread";

export function emitCanvasOpenCommentThread(threadId: string) {
  window.dispatchEvent(
    new CustomEvent(CANVAS_OPEN_COMMENT_THREAD_EVENT, { detail: { threadId } }),
  );
}

export function emitCanvasResolveCommentThread(
  threadId: string,
  resolved: boolean,
) {
  window.dispatchEvent(
    new CustomEvent(CANVAS_RESOLVE_COMMENT_THREAD_EVENT, {
      detail: { threadId, resolved },
    }),
  );
}

/**
 * Client-only UI state for canvas comments: which thread is expanded, and
 * where an unsent draft pin sits.
 *
 * Nothing here persists. A thread window is open for the person who opened it
 * and for as long as they leave it open — deliberately the opposite of quick
 * chat's `data.expanded`, which is board state everyone sees.
 *
 * Mounted in `CanvasProvider`, above the page: the entry points that place a
 * draft (keybinding, insert menu, chrome verb, node context menu) all live in
 * `CanvasPage`'s own body or in a node.
 */
type CanvasCommentsContextValue = {
  openThreadId: string | null;
  /** The pending new thread's placement — anchor included when it snapped. */
  draft: ThreadPlacement | null;
  openThread: (threadId: string) => void;
  closeThread: () => void;
  /**
   * A click on a thread's own pin: opens it, or closes it again while its
   * window sits unmoved on the pin. The pin owns this decision because it owns
   * the pointerup it lands on; see `pinClickClosesThreadWindow`.
   */
  clickThreadPin: (threadId: string) => void;
  /**
   * The open window handing up the session it owns, so a pin click can read
   * whether that window has been moved. A ref, not state: nothing here renders
   * from it, and a window drag would otherwise re-render every consumer.
   */
  reportThreadWindowSession: (session: ThreadWindowSession | null) => void;
  placeDraftAt: (placement: ThreadPlacement) => void;
  cancelPlacement: () => void;
};

const CanvasCommentsContext = createContext<CanvasCommentsContextValue | null>(
  null,
);

export function CanvasCommentsProvider({ children }: PropsWithChildren) {
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ThreadPlacement | null>(null);
  const sessionRef = useRef<ThreadWindowSession | null>(null);

  // A draft and an expanded thread are the same gesture at two stages, so the
  // board never carries both.
  const openThread = useCallback((threadId: string) => {
    setOpenThreadId(threadId);
    setDraft(null);
  }, []);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const { threadId } = (e as CustomEvent).detail as { threadId?: string };
      if (threadId) openThread(threadId);
    };
    window.addEventListener(CANVAS_OPEN_COMMENT_THREAD_EVENT, onOpen);
    return () =>
      window.removeEventListener(CANVAS_OPEN_COMMENT_THREAD_EVENT, onOpen);
  }, [openThread]);

  const markResolved = useMarkThreadAsResolved();
  const markUnresolved = useMarkThreadAsUnresolved();

  useEffect(() => {
    const onResolve = (e: Event) => {
      const { threadId, resolved } = (e as CustomEvent).detail as {
        threadId?: string;
        resolved?: boolean;
      };
      if (!threadId) return;
      if (resolved) {
        markResolved(threadId);
        setOpenThreadId((open) => (open === threadId ? null : open));
      } else {
        markUnresolved(threadId);
      }
    };
    window.addEventListener(CANVAS_RESOLVE_COMMENT_THREAD_EVENT, onResolve);
    return () =>
      window.removeEventListener(
        CANVAS_RESOLVE_COMMENT_THREAD_EVENT,
        onResolve,
      );
  }, [markResolved, markUnresolved]);

  const value = useMemo<CanvasCommentsContextValue>(
    () => ({
      openThreadId,
      draft,
      openThread,
      closeThread: () => setOpenThreadId(null),
      clickThreadPin: (threadId: string) => {
        if (pinClickClosesThreadWindow(sessionRef.current, threadId)) {
          setOpenThreadId(null);
          return;
        }
        openThread(threadId);
      },
      reportThreadWindowSession: (session: ThreadWindowSession | null) => {
        sessionRef.current = session;
      },
      placeDraftAt: (placement: ThreadPlacement) => {
        setDraft(placement);
        setOpenThreadId(null);
      },
      cancelPlacement: () => setDraft(null),
    }),
    [openThreadId, draft, openThread],
  );

  return (
    <CanvasCommentsContext.Provider value={value}>
      {children}
    </CanvasCommentsContext.Provider>
  );
}

/** Null outside the provider, so surfaces that mount without it can no-op. */
export function useCanvasCommentsOptional() {
  return useContext(CanvasCommentsContext);
}

export function useCanvasComments(): CanvasCommentsContextValue {
  const ctx = useContext(CanvasCommentsContext);
  if (!ctx) {
    throw new Error(
      "useCanvasComments must be used within a CanvasCommentsProvider",
    );
  }
  return ctx;
}
